// =============================================================================
// GESTION DES MANDATAIRES ANAH / MPR
// =============================================================================
// Liste des mandataires avec :
// - Recherche SIREN auto-remplissage
// - Upload signature/tampon
// - Sélection du mandataire par défaut
// =============================================================================

import { useState, useEffect, useCallback } from "react";
import {
  Building2, User, Save, Loader2, CheckCircle2, Plus, Trash2,
  AlertCircle, Info, MapPin, Phone, Mail, FileCheck, Search,
  Stamp, PenTool, Upload, Image, X, Edit, Star
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "../../components/ui/dialog";
import { toast } from "sonner";
import { EntrepriseSearch } from "../../components/metier/EntrepriseSearch";
import { AdresseAutocomplete } from "../../components/metier/AdresseAutocomplete";
import { useConfigList } from "../../hooks/useConfigList";
import { formatClientFullName } from "../../utils/clientFormat";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MandataireMPR({ tenantId = "climatpac" }) {
  const { items: civiliteOptions } = useConfigList("civilites");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mandataires, setMandataires] = useState([]);
  const [defaultMandataireId, setDefaultMandataireId] = useState(null);
  
  // Listes dynamiques chargées depuis la base
  const [listes, setListes] = useState({
    types_mandataire: [],
    civilites: []
  });
  
  // Dialog création/édition
  const [showDialog, setShowDialog] = useState(false);
  const [editingMandataire, setEditingMandataire] = useState(null);
  const [formData, setFormData] = useState({
    civilite: "M.",
    nom: "",
    prenom: "",
    raison_sociale: "",
    siren: "",
    siret: "",
    forme_juridique: "",
    adresse: "",
    code_postal: "",
    ville: "",
    email: "",
    telephone: "",
    numero_agrement_anah: "",
    type_mandataire: "entreprise",
    signature_url: null,
    tampon_url: null
  });
  
  // Upload
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [uploadingTampon, setUploadingTampon] = useState(false);

  // Fonction pour obtenir les headers avec authentification (toujours frais)
  const getAuthHeaders = () => {
    const token = localStorage.getItem("session_token");
    return {
      ...enteteTenant(tenantId),
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  // Charger les mandataires et les listes
  useEffect(() => {
    loadMandataires();
    loadListes();
  }, [tenantId]);

  const loadListes = async () => {
    try {
      const res = await fetch(`${API}/api/metier/documents-engine/admin/listes`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setListes({
          types_mandataire: data.listes?.types_mandataire || [],
          civilites: data.listes?.civilites || []
        });
      }
    } catch (err) {
      console.error("Erreur chargement listes:", err);
    }
  };

  const loadMandataires = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/platform/mandataires`, {
        headers: getAuthHeaders()
      });

      if (res.ok) {
        const data = await res.json();
        setMandataires(data.mandataires || []);
        setDefaultMandataireId(data.default_id);
      }
    } catch (err) {
      console.error("Erreur chargement mandataires:", err);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  // Sélection entreprise depuis recherche SIREN
  const handleSelectEntreprise = (entreprise) => {
    setFormData(prev => ({
      ...prev,
      raison_sociale: entreprise.raison_sociale,
      siren: entreprise.siren,
      siret: entreprise.siret || "",
      forme_juridique: entreprise.forme_juridique,
      adresse: entreprise.adresse,
      code_postal: entreprise.code_postal,
      ville: entreprise.ville,
      // Si dirigeant disponible, pré-remplir
      ...(entreprise.dirigeant && {
        prenom: entreprise.dirigeant.split(' ')[0] || "",
        nom: entreprise.dirigeant.split(' ').slice(1).join(' ') || ""
      })
    }));
  };

  // Sélection adresse
  const handleSelectAdresse = (adresse) => {
    setFormData(prev => ({
      ...prev,
      adresse: adresse.rue || adresse.adresse_complete,
      code_postal: adresse.code_postal,
      ville: adresse.ville
    }));
  };

  // Upload signature ou tampon
  const handleUpload = async (file, type) => {
    if (!file) return;
    
    const isSignature = type === 'signature';
    isSignature ? setUploadingSignature(true) : setUploadingTampon(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('type', type);
      
      const token = localStorage.getItem("session_token");
      const res = await fetch(`${API}/api/platform/mandataires/upload-image`, {
        method: 'POST',
        headers: {
          ...enteteTenant(tenantId),
          ...(token && { "Authorization": `Bearer ${token}` })
        },
        body: formDataUpload
      });

      if (res.ok) {
        const data = await res.json();
        setFormData(prev => ({
          ...prev,
          [isSignature ? 'signature_url' : 'tampon_url']: data.url
        }));
        toast.success(`${isSignature ? 'Signature' : 'Tampon'} uploadé`);
      } else {
        toast.error("Erreur lors de l'upload");
      }
    } catch (err) {
      console.error("Erreur upload:", err);
      toast.error("Erreur de connexion");
    } finally {
      isSignature ? setUploadingSignature(false) : setUploadingTampon(false);
    }
  };

  // Ouvrir le dialog de création
  const openCreateDialog = () => {
    setEditingMandataire(null);
    setFormData({
      civilite: "M.",
      nom: "",
      prenom: "",
      raison_sociale: "",
      siren: "",
      siret: "",
      forme_juridique: "",
      adresse: "",
      code_postal: "",
      ville: "",
      email: "",
      telephone: "",
      numero_agrement_anah: "",
      type_mandataire: "entreprise",
      signature_url: null,
      tampon_url: null
    });
    setShowDialog(true);
  };

  // Ouvrir le dialog d'édition
  const openEditDialog = (mandataire) => {
    setEditingMandataire(mandataire);
    setFormData(mandataire);
    setShowDialog(true);
  };

  // Sauvegarder
  const handleSave = async () => {
    // Validation
    if (!formData.raison_sociale) {
      toast.error("Raison sociale requise");
      return;
    }
    if (!formData.nom || !formData.prenom) {
      toast.error("Nom et prénom du représentant requis");
      return;
    }

    setSaving(true);
    try {
      const url = editingMandataire 
        ? `${API}/api/platform/mandataires/${editingMandataire.id}`
        : `${API}/api/platform/mandataires`;
      
      const res = await fetch(url, {
        method: editingMandataire ? "PUT" : "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success(editingMandataire ? "Mandataire mis à jour" : "Mandataire créé");
        setShowDialog(false);
        loadMandataires();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Erreur lors de l'enregistrement");
      }
    } catch (err) {
      console.error("Erreur sauvegarde:", err);
      toast.error("Erreur de connexion");
    } finally {
      setSaving(false);
    }
  };

  // Supprimer un mandataire
  const handleDelete = async (id) => {
    if (!confirm("Supprimer ce mandataire ?")) return;
    
    try {
      const res = await fetch(`${API}/api/platform/mandataires/${id}`, {
        method: "DELETE",
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success("Mandataire supprimé");
        loadMandataires();
      }
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  // Définir comme mandataire par défaut
  const handleSetDefault = async (id) => {
    try {
      const res = await fetch(`${API}/api/platform/mandataires/${id}/set-default`, {
        method: "POST",
        headers: getAuthHeaders()
      });

      if (res.ok) {
        toast.success("Mandataire par défaut mis à jour");
        setDefaultMandataireId(id);
      }
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const updateField = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--oh-text-muted)]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <div className="bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)] rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-[var(--oh-accent-text)] mt-0.5" />
          <div>
            <p className="text-[var(--oh-accent-text)] font-medium">Mandataires ANAH / MaPrimeRénov'</p>
            <p className="text-[var(--oh-accent-text)] text-sm mt-1">
              Gérez ici les mandataires qui peuvent agir au nom des bénéficiaires auprès de l'ANAH.
              Les données sont utilisées pour remplir automatiquement les formulaires officiels MPR.
            </p>
          </div>
        </div>
      </div>

      {/* Bouton Ajouter */}
      <div className="flex justify-end">
        <Button
          onClick={openCreateDialog}
          className="bg-emerald-600 hover:bg-emerald-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau mandataire
        </Button>
      </div>

      {/* Liste des mandataires */}
      {mandataires.length === 0 ? (
        <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <CardContent className="py-12 text-center">
            <Building2 className="w-12 h-12 mx-auto text-[var(--oh-text-secondary)] mb-4" />
            <p className="text-[var(--oh-text-muted)]">Aucun mandataire configuré</p>
            <p className="text-[var(--oh-text-secondary)] text-sm mt-1">
              Ajoutez un mandataire pour remplir automatiquement les formulaires MPR
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {mandataires.map((m) => (
            <Card 
              key={m.id} 
              className={`bg-[var(--oh-bg-elevated)] border-[var(--oh-border)] ${
                m.id === defaultMandataireId ? 'ring-2 ring-emerald-500/50' : ''
              }`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    {/* Logo/Signature */}
                    <div className="w-16 h-16 bg-[var(--oh-bg-elevated)] rounded-lg flex items-center justify-center overflow-hidden">
                      {m.signature_url ? (
                        <img src={m.signature_url} alt="Signature" className="w-full h-full object-contain" />
                      ) : (
                        <Building2 className="w-8 h-8 text-[var(--oh-text-secondary)]" />
                      )}
                    </div>
                    
                    {/* Infos */}
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="text-[var(--oh-text)] font-medium">{m.raison_sociale}</h3>
                        {m.id === defaultMandataireId && (
                          <Badge className="bg-[var(--oh-success-bg)] text-[var(--oh-success-text)] text-xs">
                            <Star className="w-3 h-3 mr-1" />
                            Par défaut
                          </Badge>
                        )}
                      </div>
                      <p className="text-[var(--oh-text-muted)] text-sm">
                        {formatClientFullName(m)}
                      </p>
                      <p className="text-[var(--oh-text-secondary)] text-xs mt-1">
                        SIRET: {m.siret || m.siren || 'Non renseigné'} • {m.code_postal} {m.ville}
                      </p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-[var(--oh-text-muted)]">
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {m.email || '-'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {m.telephone || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    {m.id !== defaultMandataireId && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleSetDefault(m.id)}
                        className="text-[var(--oh-text-muted)] hover:text-[var(--oh-success-text)]"
                      >
                        <Star className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => openEditDialog(m)}
                      className="text-[var(--oh-text-muted)] hover:text-[var(--oh-text)]"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDelete(m.id)}
                      className="text-[var(--oh-text-muted)] hover:text-[var(--oh-error-text)]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog création/édition */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl bg-[var(--oh-bg-surface)] border-[var(--oh-border)] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)] flex items-center gap-2">
              <Building2 className="w-5 h-5 text-[var(--oh-success-text)]" />
              {editingMandataire ? "Modifier le mandataire" : "Nouveau mandataire"}
            </DialogTitle>
            <DialogDescription className="text-[var(--oh-text-muted)]">
              Recherchez par SIREN pour auto-remplir les données
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Recherche SIREN */}
            <div>
              <Label className="text-[var(--oh-text-muted)] mb-2 block">Recherche entreprise (SIREN)</Label>
              <EntrepriseSearch
                value={formData.raison_sociale}
                onChange={(v) => updateField("raison_sociale", v)}
                onSelect={handleSelectEntreprise}
                placeholder="Tapez un nom ou SIREN..."
              />
            </div>

            {/* Entreprise */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-[var(--oh-text)] text-sm flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[var(--oh-accent-text)]" />
                  Entreprise
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label className="text-[var(--oh-text-muted)] text-xs">Raison sociale *</Label>
                  <Input
                    value={formData.raison_sociale}
                    onChange={(e) => updateField("raison_sociale", e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                </div>
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-xs">SIREN</Label>
                  <Input
                    value={formData.siren}
                    onChange={(e) => updateField("siren", e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                </div>
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-xs">SIRET</Label>
                  <Input
                    value={formData.siret}
                    onChange={(e) => updateField("siret", e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                </div>
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-xs">Type</Label>
                  <Select
                    value={formData.type_mandataire}
                    onValueChange={(v) => updateField("type_mandataire", v)}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {listes.types_mandataire.length > 0 
                        ? listes.types_mandataire.filter(t => t.actif !== false).map(type => (
                            <SelectItem key={type.code} value={type.code}>{type.label}</SelectItem>
                          ))
                        : <>
                            <SelectItem value="entreprise">Entreprise RGE</SelectItem>
                            <SelectItem value="accompagnateur">Accompagnateur Rénov'</SelectItem>
                            <SelectItem value="mandataire_agree">Mandataire agréé ANAH</SelectItem>
                          </>
                      }
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-xs">N° Agrément ANAH</Label>
                  <Input
                    value={formData.numero_agrement_anah}
                    onChange={(e) => updateField("numero_agrement_anah", e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Représentant */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-[var(--oh-text)] text-sm flex items-center gap-2">
                  <User className="w-4 h-4 text-[var(--oh-success-text)]" />
                  Représentant légal
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-xs">Civilité</Label>
                  <Select
                    value={formData.civilite}
                    onValueChange={(v) => updateField("civilite", v)}
                  >
                    <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {civiliteOptions.length > 0
                        ? civiliteOptions.map(c => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)
                        : <><SelectItem value="M.">M.</SelectItem><SelectItem value="Mme">Mme</SelectItem></>
                      }
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-xs">Prénom *</Label>
                  <Input
                    value={formData.prenom}
                    onChange={(e) => updateField("prenom", e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                </div>
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-xs">Nom *</Label>
                  <Input
                    value={formData.nom}
                    onChange={(e) => updateField("nom", e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Adresse */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-[var(--oh-text)] text-sm flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-[var(--oh-info-text)]" />
                  Adresse
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <div className="col-span-3">
                  <Label className="text-[var(--oh-text-muted)] text-xs">Adresse</Label>
                  <AdresseAutocomplete
                    value={formData.adresse}
                    onChange={(v) => updateField("adresse", v)}
                    onSelect={handleSelectAdresse}
                  />
                </div>
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-xs">Code Postal</Label>
                  <Input
                    value={formData.code_postal}
                    onChange={(e) => updateField("code_postal", e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                </div>
                <div className="col-span-2">
                  <Label className="text-[var(--oh-text-muted)] text-xs">Ville</Label>
                  <Input
                    value={formData.ville}
                    onChange={(e) => updateField("ville", e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Contact */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-[var(--oh-text)] text-sm flex items-center gap-2">
                  <Phone className="w-4 h-4 text-[var(--oh-success-text)]" />
                  Contact
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-xs">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField("email", e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                </div>
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-xs">Téléphone</Label>
                  <Input
                    value={formData.telephone}
                    onChange={(e) => updateField("telephone", e.target.value)}
                    className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Signature et Tampon */}
            <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
              <CardHeader className="pb-2">
                <CardTitle className="text-[var(--oh-text)] text-sm flex items-center gap-2">
                  <PenTool className="w-4 h-4 text-[var(--oh-accent-text)]" />
                  Signature & Tampon
                </CardTitle>
                <CardDescription className="text-[var(--oh-text-secondary)] text-xs">
                  Ces images seront apposées automatiquement sur les documents
                </CardDescription>
              </CardHeader>
              <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* Signature */}
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-xs mb-2 block">Signature</Label>
                  <div className="border-2 border-dashed border-[var(--oh-border)] rounded-lg p-4 text-center">
                    {formData.signature_url ? (
                      <div className="relative">
                        <img 
                          src={formData.signature_url} 
                          alt="Signature" 
                          className="max-h-24 mx-auto"
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="absolute -top-2 -right-2 text-[var(--oh-error-text)] hover:text-[var(--oh-error-text)]"
                          onClick={() => updateField("signature_url", null)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleUpload(e.target.files[0], 'signature')}
                        />
                        {uploadingSignature ? (
                          <Loader2 className="w-8 h-8 mx-auto text-[var(--oh-text-muted)] animate-spin" />
                        ) : (
                          <>
                            <PenTool className="w-8 h-8 mx-auto text-[var(--oh-text-secondary)] mb-2" />
                            <p className="text-[var(--oh-text-muted)] text-xs">Cliquez pour uploader</p>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                </div>
                
                {/* Tampon */}
                <div>
                  <Label className="text-[var(--oh-text-muted)] text-xs mb-2 block">Tampon / Cachet</Label>
                  <div className="border-2 border-dashed border-[var(--oh-border)] rounded-lg p-4 text-center">
                    {formData.tampon_url ? (
                      <div className="relative">
                        <img 
                          src={formData.tampon_url} 
                          alt="Tampon" 
                          className="max-h-24 mx-auto"
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          className="absolute -top-2 -right-2 text-[var(--oh-error-text)] hover:text-[var(--oh-error-text)]"
                          onClick={() => updateField("tampon_url", null)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ) : (
                      <label className="cursor-pointer block">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => handleUpload(e.target.files[0], 'tampon')}
                        />
                        {uploadingTampon ? (
                          <Loader2 className="w-8 h-8 mx-auto text-[var(--oh-text-muted)] animate-spin" />
                        ) : (
                          <>
                            <Stamp className="w-8 h-8 mx-auto text-[var(--oh-text-secondary)] mb-2" />
                            <p className="text-[var(--oh-text-muted)] text-xs">Cliquez pour uploader</p>
                          </>
                        )}
                      </label>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowDialog(false)}
              className="border-[var(--oh-border)]"
            >
              Annuler
            </Button>
            <Button 
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
